import type { QuestionBoxVideoMentionData } from "./QuestionBoxVideoMention";
import type { DocMentionData } from "./questionDocument";

/**
 * Inline-JSON representation of the QuestionBox's mentions: the wire format for
 * both the submitted prompt and the clipboard, so an atom survives a round trip
 * through either.
 *
 * The canonical shapes are
 * `{"mention":"video","id":"…","start_time_sec":1,"end_time_sec":2}` and
 * `{"mention":"social_account","handle":"…","platform":"…","id":"…"}`
 * (`influencer` in place of `social_account` for a person rather than an
 * account). This is the same format the backend parses — see
 * `App\Questions\QuestionPromptMentions` — and that the chat layer reads back
 * out of a stored prompt (`parseMentionJsonInText`).
 *
 * Video mentions carry `title` and `thumbnail_url` when known so a paste, or a
 * prompt handed from one page to another, can rebuild the pill's label and
 * thumbnail without a lookup. The thumbnail never leaves the browser: see
 * promptWithoutVideoThumbnails.
 *
 * Parsing is deliberately more liberal than writing: numeric `id`s (the backend's
 * internal form) and account mentions without a `platform` are accepted, since
 * refusing to read our own or the backend's output would leave raw JSON sitting
 * in the input.
 */

export type PromptSegment =
	| { type: "text"; value: string }
	| { type: "video"; value: QuestionBoxVideoMentionData }
	| { type: "account"; value: DocMentionData };

export function serializeVideoMention(
	data: QuestionBoxVideoMentionData,
): string {
	const payload: {
		mention: "video";
		id: string;
		start_time_sec: number | null;
		end_time_sec: number | null;
		title?: string;
		thumbnail_url?: string;
	} = {
		mention: "video",
		id: data.id,
		start_time_sec: data.startTimeSec ?? null,
		end_time_sec: data.endTimeSec ?? null,
	};
	if (data.label) {
		payload.title = data.label;
	}
	if (data.thumbnailUrl) {
		payload.thumbnail_url = data.thumbnailUrl;
	}
	return JSON.stringify(payload);
}

/** Mention types naming an account or a person, as opposed to a video. */
const ACCOUNT_MENTION_TYPES = ["social_account", "influencer"];

export function serializeAccountMention(data: DocMentionData): string {
	// `platform` and `id` are dropped by JSON.stringify when undefined, which is
	// what the backend expects of an unresolved mention.
	return JSON.stringify({
		mention: data.type,
		handle: data.handle,
		platform: data.platform,
		id: data.id,
	});
}

/** A mention's `id` as the model holds it; numeric ids are the backend's form. */
function optionalId(value: unknown): string | undefined {
	if (typeof value === "string") return value.length > 0 ? value : undefined;
	return typeof value === "number" ? String(value) : undefined;
}

function toVideoMentionData(
	payload: Record<string, unknown>,
): QuestionBoxVideoMentionData | null {
	if (payload.mention !== "video") {
		return null;
	}
	const id = payload.id;
	if (typeof id !== "string" && typeof id !== "number") {
		return null;
	}
	const title = typeof payload.title === "string" ? payload.title.trim() : "";
	return {
		id: String(id),
		label: title.length > 0 ? title : "Video",
		thumbnailUrl:
			typeof payload.thumbnail_url === "string" ? payload.thumbnail_url : null,
		startTimeSec:
			typeof payload.start_time_sec === "number"
				? payload.start_time_sec
				: null,
		endTimeSec:
			typeof payload.end_time_sec === "number" ? payload.end_time_sec : null,
	};
}

function toAccountMentionData(
	payload: Record<string, unknown>,
): DocMentionData | null {
	const type = payload.mention;
	// Only the types the backend recognises become chips, so an unknown
	// `{"mention":…}` object stays literal text rather than turning into an atom
	// the prompt can't express.
	if (typeof type !== "string" || !ACCOUNT_MENTION_TYPES.includes(type)) {
		return null;
	}
	const handle = payload.handle;
	if (typeof handle !== "string" || handle.length === 0) {
		return null;
	}
	return {
		type,
		handle,
		platform:
			typeof payload.platform === "string" && payload.platform.length > 0
				? payload.platform
				: undefined,
		id: optionalId(payload.id),
	};
}

/**
 * The mention segment a decoded payload describes, or null when it is not a
 * mention we can render. Shared by the two transports that carry mentions:
 * inline JSON (below) and mention URLs (see mentionUrl).
 */
export function mentionFromPayload(
	payload: Record<string, unknown>,
): Exclude<PromptSegment, { type: "text" }> | null {
	const video = toVideoMentionData(payload);
	if (video) return { type: "video", value: video };
	const account = toAccountMentionData(payload);
	return account ? { type: "account", value: account } : null;
}

/**
 * Splits `text` into plain-text runs and `{"mention":…}` blocks, each block
 * still raw. Brace matching is naive (does not skip braces inside JSON
 * strings), which is fine for the values we emit.
 */
function splitMentionBlocks(text: string): { block: boolean; value: string }[] {
	const parts: { block: boolean; value: string }[] = [];
	const marker = /\{\s*"mention"\s*:/g;
	let i = 0;

	while (i < text.length) {
		marker.lastIndex = i;
		const match = marker.exec(text);
		if (!match) {
			parts.push({ block: false, value: text.slice(i) });
			break;
		}

		const start = match.index;
		if (start > i) {
			parts.push({ block: false, value: text.slice(i, start) });
		}

		let depth = 0;
		let j = start;
		for (; j < text.length; j++) {
			const c = text[j];
			if (c === "{") {
				depth++;
			} else if (c === "}") {
				depth--;
				if (depth === 0) {
					j++;
					break;
				}
			}
		}

		parts.push({ block: true, value: text.slice(start, j) });
		i = j;
	}

	return parts;
}

const parseMentionBlock = (block: string): Record<string, unknown> | null => {
	try {
		const parsed: unknown = JSON.parse(block);
		return parsed !== null && typeof parsed === "object"
			? (parsed as Record<string, unknown>)
			: null;
	} catch {
		return null;
	}
};

/**
 * Splits pasted text into plain-text runs and mention payloads; anything that
 * fails to parse as a mention is preserved verbatim as text.
 */
export function parsePromptSegments(text: string): PromptSegment[] {
	return splitMentionBlocks(text).map(({ block, value }) => {
		const payload = block ? parseMentionBlock(value) : null;
		const mention = payload ? mentionFromPayload(payload) : null;
		return mention ?? { type: "text", value };
	});
}

/**
 * `prompt` as it is sent to the server. A video mention's `thumbnail_url` is a
 * short-lived presigned URL that only the pill on screen needs, and it must not
 * end up stored in the question.
 */
export function promptWithoutVideoThumbnails(prompt: string): string {
	return splitMentionBlocks(prompt)
		.map(({ block, value }) => {
			const payload = block ? parseMentionBlock(value) : null;
			if (payload?.mention !== "video" || !("thumbnail_url" in payload)) {
				return value;
			}
			const { thumbnail_url: _, ...rest } = payload;
			return JSON.stringify(rest);
		})
		.join("");
}
